/**
 * DiscountOverridesPanel — audit trail of every manager-approved POS discount.
 * Filters: date range + manager. Available in Reports (admins/managers see this).
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { inr, fmtDate } from "@/lib/utils-format";
import { ShieldCheck, RefreshCw, Download, Search } from "lucide-react";

export default function DiscountOverridesPanel() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [managerId, setManagerId] = useState("all");
  const [appliedOnly, setAppliedOnly] = useState("all");   // "all" | "applied"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (managerId && managerId !== "all") params.manager_id = managerId;
      if (appliedOnly === "applied") params.applied_only = true;
      const r = await api.get("/pos/discount-overrides", { params });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load overrides"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const managers = useMemo(() => {
    if (!data?.overrides) return [];
    const seen = new Map();
    data.overrides.forEach((o) => { if (o.manager_id && !seen.has(o.manager_id)) seen.set(o.manager_id, o.manager_name || o.manager_email); });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [data]);

  const exportCsv = () => {
    if (!data?.overrides?.length) return toast.error("No records to export");
    const header = ["Date/Time", "Sale #", "Cashier", "Manager", "Reason", "Method", "Discount ₹", "Grand Total ₹", "Applied", "Branch"];
    const rows = data.overrides.map((o) => [
      o.authorized_at, o.sale_number || "-", o.cashier_name, o.manager_name, o.reason,
      o.method, o.discount_amount || 0, o.grand_total || 0, o.applied ? "YES" : "NO", o.branch_id || "-",
    ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `discount-overrides-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  return (
    <Card className="border-[#EAE5D9]" data-testid="discount-overrides-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 flex-wrap gap-2">
        <CardTitle className="font-[Outfit] text-lg flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-amber-700" /> Discount Overrides — Audit Trail
        </CardTitle>
        <div className="flex flex-wrap items-end gap-2">
          <div><Label className="text-[10px] uppercase text-[#8B7355]">From</Label><Input type="date" className="h-8 w-36 bg-white" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="do-from" /></div>
          <div><Label className="text-[10px] uppercase text-[#8B7355]">To</Label><Input type="date" className="h-8 w-36 bg-white" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="do-to" /></div>
          <div>
            <Label className="text-[10px] uppercase text-[#8B7355]">Manager</Label>
            <Select value={managerId} onValueChange={setManagerId}>
              <SelectTrigger className="h-8 w-44 bg-white" data-testid="do-manager"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All managers</SelectItem>
                {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-[#8B7355]">Status</Label>
            <Select value={appliedOnly} onValueChange={setAppliedOnly}>
              <SelectTrigger className="h-8 w-32 bg-white" data-testid="do-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="applied">Applied only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="do-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Apply
          </Button>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="do-export">
            <Download className="w-3.5 h-3.5 mr-1" /> CSV
          </Button>
        </div>
      </CardHeader>

      {data && (
        <div className="px-6 pb-3 flex flex-wrap gap-3 text-xs text-[#5C4033]" data-testid="do-summary">
          <Badge variant="outline" className="bg-[#FAF6EE]">Total overrides: <b className="ml-1">{data.count}</b></Badge>
          <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-800">Applied to sales: <b className="ml-1">{data.applied_count}</b></Badge>
          <Badge variant="outline" className="bg-red-50 border-red-200 text-red-800">Total discount value: <b className="ml-1">{inr(data.total_discount_amount || 0)}</b></Badge>
        </div>
      )}

      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left uc-label border-b border-[#EAE5D9]">
                <th className="px-4 py-3">Date / Time</th>
                <th className="px-4 py-3">Sale #</th>
                <th className="px-4 py-3">Cashier</th>
                <th className="px-4 py-3">Approved by (Manager)</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3 text-right">Discount</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody data-testid="do-rows">
              {(data?.overrides || []).map((o) => (
                <tr key={o.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`do-row-${o.id}`}>
                  <td className="px-4 py-2 text-xs text-[#8B7355]">{fmtDate(o.authorized_at)} <div className="text-[10px]">{(o.authorized_at || "").slice(11, 19)}</div></td>
                  <td className="px-4 py-2 font-mono text-xs">{o.sale_number || <span className="text-[#8B7355] italic">not-applied</span>}</td>
                  <td className="px-4 py-2">{o.cashier_name}</td>
                  <td className="px-4 py-2 font-medium text-[#2C1810]">{o.manager_name}</td>
                  <td className="px-4 py-2 text-xs max-w-xs">{o.reason || <span className="text-red-600 italic">missing</span>}</td>
                  <td className="px-4 py-2">
                    <Badge variant="outline" className={`uppercase text-[10px] ${o.method === "pin" ? "bg-slate-100 text-slate-700" : "bg-amber-100 text-amber-800 border-amber-200"}`}>{o.method}</Badge>
                  </td>
                  <td className="px-4 py-2 text-right font-semibold text-red-700">{o.discount_amount ? inr(o.discount_amount) : "—"}</td>
                  <td className="px-4 py-2">
                    {o.applied
                      ? <Badge className="bg-emerald-600 text-white border-0">applied</Badge>
                      : <Badge variant="outline" className="text-[#8B7355]">not applied</Badge>}
                  </td>
                </tr>
              ))}
              {(!data?.overrides || data.overrides.length === 0) && (
                <tr><td colSpan={8} className="text-center py-10 text-[#8B7355]">
                  <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  No manager-approved discount overrides in this period.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
